import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { AuthProvider, useAuth } from './context/AuthContext';
import dashboardLogoUrl from './assets/dashboard-logo.png';
import { FiFolderPlus, FiUsers, FiMap, FiDownload, FiChevronDown, FiLock, FiDatabase, FiActivity } from 'react-icons/fi';
import Login from './components/Login';
import MapView from './components/MapView';
import ManageProjectsModal from './components/ManageProjectsModal';
import StatusOverview from './components/StatusOverview';
import UserManagement from './components/UserManagement';
import ActivityLogModal from './components/ActivityLogModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import BackupRestore from './components/BackupRestore';
import { getProjects as fetchProjectsAPI, getProject as fetchProjectAPI, downloadProjectReport } from './services/api';
import { getVisibleBlocks } from './utils/layerUtils';
import './App.css';

function Dashboard() {
  const { user, canCreate, logout } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    // Restore last selected project from localStorage on mount
    const saved = localStorage.getItem('selectedProjectId');
    return saved ? parseInt(saved, 10) : null;
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(350);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [showManageProjectsModal, setShowManageProjectsModal] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showHtmlReportModal, setShowHtmlReportModal] = useState(false);
  const [htmlReportUrl, setHtmlReportUrl] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showBackupRestoreModal, setShowBackupRestoreModal] = useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [selectedKmlLayerIds, setSelectedKmlLayerIds] = useState(null);
  const [showBlockLabelsOnMap, setShowBlockLabelsOnMap] = useState(() => {
    const saved = localStorage.getItem('showBlockLabelsOnMap');
    if (saved === null) return true;
    return saved === '1' || saved === 'true';
  });
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  useEffect(() => {
    setSelectedKmlLayerIds(null);
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('selectedProjectId', selectedProjectId.toString());
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  }, [selectedProjectId]);

  useEffect(() => {
    localStorage.setItem('showBlockLabelsOnMap', showBlockLabelsOnMap ? '1' : '0');
  }, [showBlockLabelsOnMap]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProject(selectedProjectId);
    }
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const data = await fetchProjectsAPI();
      console.log('Fetched projects:', data);
      setProjects(data);
      
      if (data.length > 0) {
        // If we have a saved selectedProjectId, check if it still exists
        if (selectedProjectId) {
          const projectExists = data.some(p => p.id === selectedProjectId);
          if (projectExists) {
            // Project still exists, keep it selected
            // fetchProject will be called by the useEffect
          } else {
            // Project no longer exists, select first one
            setSelectedProjectId(data[0].id);
          }
        } else {
          // No saved selection, select first project
          setSelectedProjectId(data[0].id);
        }
      } else {
        setCurrentProject(null);
        setSelectedProjectId(null);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      console.error('Error response:', error.response?.data);
      // Show error to user
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication error. Please login again.');
      } else {
        toast.error('Failed to load projects. Please refresh the page.');
      }
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchProject = async (projectId) => {
    try {
      const data = await fetchProjectAPI(projectId);
      if (selectedProjectIdRef.current !== projectId) {
        return;
      }
      setCurrentProject(data);
    } catch (error) {
      console.error('Error fetching project:', error);
      setCurrentProject(null);
      setSelectedProjectId(null);
      fetchProjects();
    }
  };

  const handleBlockStatusUpdate = () => {
    if (selectedProjectId) {
      fetchProject(selectedProjectId);
    }
  };

  const visibleBlocks = useMemo(() => {
    if (!currentProject) return [];
    return getVisibleBlocks(currentProject, selectedKmlLayerIds);
  }, [currentProject, selectedKmlLayerIds]);

  const handleDownloadReport = async (format) => {
    if (!selectedProjectId || downloadingReport || !currentProject) {
      return;
    }

    const reportOpts = { showBlockLabels: showBlockLabelsOnMap };
    if (Array.isArray(selectedKmlLayerIds)) {
      reportOpts.kmlLayerIds = selectedKmlLayerIds;
    }

    // Handle HTML report separately - show in new window
    if (format === 'html') {
      setShowReportDropdown(false);
      setDownloadingReport(true);
      try {
        // Fetch HTML content with authentication
        const htmlContent = await downloadProjectReport(selectedProjectId, 'html', reportOpts);
        
        // Create a blob URL and open in new window
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const reportWindow = window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
        
        if (!reportWindow) {
          toast.error('Please allow popups to view the HTML report');
          window.URL.revokeObjectURL(url);
        } else {
          toast.success('HTML report opened in new window');
          // Clean up URL after window loads
          reportWindow.addEventListener('load', () => {
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
          });
        }
      } catch (error) {
        toast.error('Failed to open HTML report: ' + (error.response?.data?.error || error.message));
      } finally {
        setDownloadingReport(false);
      }
      return;
    }

    setDownloadingReport(true);
    setShowReportDropdown(false);

    try {
      const blob = await downloadProjectReport(selectedProjectId, format, reportOpts);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const projectName = currentProject?.name || 'project';
      const extension = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      link.download = `${projectName.replace(/[^a-z0-9]/gi, '_')}_report.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report downloaded successfully as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to download report: ' + (error.response?.data?.error || error.message));
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleSidebarMouseDown = (e) => {
    setIsResizingSidebar(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingSidebar) return;
      
      const newWidth = e.clientX;
      
      // Limit width between 250px and 50% of viewport
      const minWidth = 250;
      const maxWidth = window.innerWidth * 0.5;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
    };

    if (isResizingSidebar) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingSidebar]);

  // Close report dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showReportDropdown && !event.target.closest('.report-download-container')) {
        setShowReportDropdown(false);
      }
    };

    if (showReportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReportDropdown]);

  const isProjectLoading =
    Boolean(selectedProjectId) &&
    (!currentProject || Number(currentProject.id) !== Number(selectedProjectId));

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-title">
          <img src={dashboardLogoUrl} alt="Logo" className="header-logo" />
          <div className="header-brand">
            <span className="header-brand-title">FalconViz</span>
            <span className="header-brand-subtitle">Drone Mapping Dashboard</span>
          </div>
        </div>
        <div className="header-right">
          <div className="project-selector">
            <label>Select Project: </label>
            <select 
              value={selectedProjectId || ''} 
              onChange={(e) => setSelectedProjectId(parseInt(e.target.value))}
            >
              <option value="">-- Select Project --</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            {selectedProjectId && (
              <label className="header-block-labels-toggle">
                <input
                  type="checkbox"
                  checked={showBlockLabelsOnMap}
                  onChange={(e) => setShowBlockLabelsOnMap(e.target.checked)}
                  title="Show numbered labels on blocks (map + PDF/HTML reports)"
                />
                <span>Block labels</span>
              </label>
            )}
            {selectedProjectId && (
              <div className="report-download-container">
                <button 
                  className="download-report-btn"
                  onClick={() => setShowReportDropdown(!showReportDropdown)}
                  disabled={downloadingReport}
                  title="Download project report"
                >
                  <FiDownload className="download-icon" />
                  <span>{downloadingReport ? 'Generating...' : 'Project Report'}</span>
                  <FiChevronDown className="dropdown-chevron" />
                </button>
                {showReportDropdown && (
                  <div className="report-dropdown">
                    <button onClick={() => handleDownloadReport('html')} disabled={downloadingReport}>
                      View HTML Report
                    </button>
                    <div className="report-dropdown-divider"></div>
                    <button onClick={() => handleDownloadReport('pdf')} disabled={downloadingReport}>
                      Download as PDF
                    </button>
                    <button onClick={() => handleDownloadReport('excel')} disabled={downloadingReport}>
                      Download as Excel
                    </button>
                    <button onClick={() => handleDownloadReport('csv')} disabled={downloadingReport}>
                      Download as CSV
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="user-info">
            <span className="user-role">{user?.role}</span>
            <span className="user-name">{user?.username}</span>
            <button 
              className="change-password-btn" 
              onClick={() => setShowChangePasswordModal(true)}
              title="Change password"
            >
              <FiLock />
            </button>
            <button className="logout-btn" onClick={() => {
              logout();
              window.location.href = '/login';
            }}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="app-content">
        <div 
          className="sidebar"
          style={{ width: `${sidebarWidth}px` }}
        >
          <div className="sidebar-actions">
            {canCreate() && (
              <button 
                className="sidebar-action-btn"
                onClick={() => setShowManageProjectsModal(true)}
                title="Manage projects, layers, and team access"
              >
                <FiFolderPlus className="sidebar-action-icon" />
                <span>Manage projects</span>
              </button>
            )}
            {user?.role === 'admin' && (
              <>
                <button 
                  className="sidebar-action-btn"
                  onClick={() => setShowUserManagementModal(true)}
                  title="Manage users and project assignments"
                >
                  <FiUsers className="sidebar-action-icon" />
                  <span>User Management</span>
                </button>
                <button 
                  className="sidebar-action-btn"
                  onClick={() => setShowActivityLogModal(true)}
                  title="View user sign-ins and key actions on the dashboard"
                >
                  <FiActivity className="sidebar-action-icon" />
                  <span>User activity</span>
                </button>
                <button 
                  className="sidebar-action-btn"
                  onClick={() => setShowBackupRestoreModal(true)}
                  title="Backup and restore dashboard data"
                >
                  <FiDatabase className="sidebar-action-icon" />
                  <span>Backup & Restore</span>
                </button>
              </>
            )}
          </div>
          {currentProject && Number(currentProject.id) === Number(selectedProjectId) && (
            <StatusOverview projectId={currentProject.id} selectedKmlLayerIds={selectedKmlLayerIds} />
          )}
        </div>
        <div 
          className="sidebar-resize-handle"
          onMouseDown={handleSidebarMouseDown}
        >
          <div className="sidebar-resize-handle-line"></div>
        </div>

        <div className="main-content">
          {isLoadingProjects ? (
            <div className="no-project">
              <div className="no-project-content">
                <div className="loading-spinner" style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #2196f3',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <p>Loading projects...</p>
              </div>
            </div>
          ) : isProjectLoading ? (
            <div className="no-project">
              <div className="no-project-content">
                <div
                  className="loading-spinner"
                  style={{
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #2196f3',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }}
                ></div>
                <p>Loading project…</p>
              </div>
            </div>
          ) : currentProject ? (
            <MapView
              project={currentProject}
              visibleBlocks={visibleBlocks}
              onBlockStatusUpdate={handleBlockStatusUpdate}
              selectedLayerIds={selectedKmlLayerIds}
              onLayerSelectionChange={setSelectedKmlLayerIds}
              showBlockLabels={showBlockLabelsOnMap}
            />
          ) : (
            <div className="no-project">
              <div className="no-project-content">
                <FiMap className="no-project-icon" />
                <h2>No Project Selected</h2>
                <p>Create a new project by uploading a KML file or select an existing project from the dropdown above.</p>
                {canCreate() && (
                  <button 
                    className="no-project-button"
                    onClick={() => setShowManageProjectsModal(true)}
                  >
                    <FiFolderPlus />
                    <span>Manage projects</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {canCreate() && (
        <ManageProjectsModal 
          isOpen={showManageProjectsModal}
          onClose={() => setShowManageProjectsModal(false)}
          projects={projects}
          onRefresh={async () => {
            await fetchProjects();
            const sid = selectedProjectIdRef.current;
            if (sid) {
              await fetchProject(sid);
            }
          }}
          onProjectSelect={(id) => setSelectedProjectId(id)}
          user={user}
        />
      )}

      {user?.role === 'admin' && (
        <UserManagement 
          isOpen={showUserManagementModal}
          onClose={() => setShowUserManagementModal(false)}
        />
      )}
      {user?.role === 'admin' && (
        <ActivityLogModal
          isOpen={showActivityLogModal}
          onClose={() => setShowActivityLogModal(false)}
        />
      )}
      <ChangePasswordModal 
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
      <BackupRestore 
        isOpen={showBackupRestoreModal}
        onClose={() => setShowBackupRestoreModal(false)}
        onRestoreComplete={() => {
          // Refresh projects after restore
          fetchProjects();
        }}
      />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </AuthProvider>
    </Router>
  );
}

export default App;

